import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getAuthHeader } from '@/utils';
import { BASE_URL } from '@core/constants';

/**
 * Fuente única de verdad del estado de tareas de transcripción activas:
 * un único registro en el cache de TanStack Query (ya montado en toda la
 * app vía QueryClientProvider), en vez de un Context nuevo. Reemplaza las
 * 3 escrituras descoordinadas a localStorage.activeTranscriptionTasks que
 * existían antes (ver useTranscriptionTasksSync.js, que persiste este
 * registro a localStorage como único escritor).
 *
 * Forma del registro: Record<taskId, TaskState>
 * TaskState = { taskId, studyId, audioId, studyName, status, progress,
 *               message, startedAt, error, errorType, transcription_id }
 */
export const TRANSCRIPTION_TASKS_QUERY_KEY = ['transcriptionTasks'];

const EMPTY_RECORD = {};

function useTranscriptionTasksRecord() {
  const queryClient = useQueryClient();

  const subscribe = useCallback(
    onStoreChange => {
      const unsubscribe = queryClient.getQueryCache().subscribe(event => {
        if (event?.query?.queryKey?.[0] === TRANSCRIPTION_TASKS_QUERY_KEY[0]) {
          onStoreChange();
        }
      });
      return unsubscribe;
    },
    [queryClient]
  );

  const getSnapshot = useCallback(
    () => queryClient.getQueryData(TRANSCRIPTION_TASKS_QUERY_KEY) ?? EMPTY_RECORD,
    [queryClient]
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Todas las tareas activas (cualquier estudio) -- usado por ActiveTasksBadge. */
export function useActiveTranscriptionTasks() {
  const record = useTranscriptionTasksRecord();
  return useMemo(() => Object.values(record), [record]);
}

/** La tarea (si existe) del estudio actual -- usado por GenerateReport.jsx. */
export function useTranscriptionTaskForStudy(studyId) {
  const record = useTranscriptionTasksRecord();
  return useMemo(() => {
    if (!studyId) return null;
    return Object.values(record).find(t => String(t.studyId) === String(studyId)) || null;
  }, [record, studyId]);
}

/**
 * Registra una tarea recién lanzada. Llamado justo después de recibir
 * task_id de upload-and-process-audio/. useTranscriptionTasksSync detecta
 * la entrada nueva (vía su suscripción al cache) y abre la conexión SSE.
 */
export function startTrackingTranscriptionTask(queryClient, taskInfo) {
  queryClient.setQueryData(TRANSCRIPTION_TASKS_QUERY_KEY, prev => ({
    ...(prev || {}),
    [taskInfo.taskId]: {
      ...taskInfo,
      status: 'pending',
      progress: 0,
      startedAt: new Date().toISOString(),
    },
  }));
}

/**
 * Cancela una tarea (revoke en backend) y la quita del registro compartido.
 * Única implementación -- reemplaza las copias que antes vivían por
 * separado en ActiveTasksBadge.jsx y ReportEditionStep.jsx.
 */
export async function cancelTranscriptionTask(queryClient, taskId) {
  let result = { revoked: false };
  try {
    const response = await fetch(`${BASE_URL}insurance/transcribe/revoke-task/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ task_id: taskId }),
    });
    if (response.ok) {
      result = await response.json();
    }
  } catch (err) {
    console.warn('[transcriptionTasks] No se pudo revocar en backend:', err);
  } finally {
    queryClient.setQueryData(TRANSCRIPTION_TASKS_QUERY_KEY, prev => {
      if (!prev || !(taskId in prev)) return prev;
      const { [taskId]: _removed, ...rest } = prev;
      return rest;
    });
  }
  return result;
}
